import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Copy, Filter, Smartphone, Hash, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const toDateInputValue = (date) => date.toISOString().slice(0, 10);

const formatAmount = (value) => Number(value || 0).toFixed(2);

const formatTimestamp = (iso) => {
  if (!iso) return '--';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '--';
  return parsed.toLocaleString();
};

const AdminTopupRequestsSection = () => {
  const {
    adminTopupRequests,
    topupRequestStats,
    refreshAdminTopupRequests,
    updateAdminTopupRequestStatus,
  } = useAuth();

  const [requestTab, setRequestTab] = useState('pending');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [noteSearch, setNoteSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [copiedKey, setCopiedKey] = useState('');

  const activeFilters = useMemo(() => {
    const filters = {};
    if (fromDate) filters.fromDate = fromDate;
    if (toDate) filters.toDate = toDate;
    return filters;
  }, [fromDate, toDate]);

  const filteredSource = useMemo(() => {
    let filtered = [...(adminTopupRequests || [])];
    if (fromDate) {
      const fromStart = new Date(`${fromDate}T00:00:00`);
      filtered = filtered.filter((item) => new Date(item.createdAt) >= fromStart);
    }
    if (toDate) {
      const toEnd = new Date(`${toDate}T23:59:59.999`);
      filtered = filtered.filter((item) => new Date(item.createdAt) <= toEnd);
    }
    return filtered;
  }, [adminTopupRequests, fromDate, toDate]);

  const displayed = useMemo(() => {
    let list;
    if (requestTab === 'pending') {
      list = filteredSource.filter((item) => String(item?.status || '').toLowerCase() === 'pending');
    } else {
      list = filteredSource.filter((item) => {
        const status = String(item?.status || '').toLowerCase();
        return status === 'approved' || status === 'rejected';
      });
    }

    const term = noteSearch.trim().toLowerCase();
    if (!term) return list;
    return list.filter((item) => {
      // Search by PAYER UPI ID — the UPI ID the user typed before pressing
      // "I Paid", so admin can quickly find every request from a given payer.
      const payerUpi = String(item?.payerUpiId || '').toLowerCase();
      return payerUpi.includes(term);
    });
  }, [filteredSource, requestTab, noteSearch]);

  const fetchRequests = async (filters = activeFilters) => {
    setLoading(true);
    setInfoMessage('');
    const result = await refreshAdminTopupRequests(filters);
    if (!result.success) {
      setInfoMessage(result.error || 'Failed to load top-up requests');
    }
    setLoading(false);
    return result;
  };

  useEffect(() => {
    fetchRequests({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setPresetRange = async (days) => {
    const now = new Date();
    const to = toDateInputValue(now);
    const fromDateObj = new Date(now);
    fromDateObj.setDate(now.getDate() - (days - 1));
    const from = toDateInputValue(fromDateObj);
    setFromDate(from);
    setToDate(to);
    await fetchRequests({ fromDate: from, toDate: to });
  };

  const resetFilters = async () => {
    setFromDate('');
    setToDate('');
    await fetchRequests({});
  };

  const handleCopy = async (key, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(''), 1200);
    } catch {
      setInfoMessage('Copy failed. Please copy manually.');
    }
  };

  const handleAction = async (request, action) => {
    let reason = '';
    if (action === 'reject') {
      const input = window.prompt('Enter rejection reason:', 'Payment not received');
      if (input === null) return;
      reason = input.trim() || 'Rejected by admin';
    }

    setProcessingId(request.id);
    const payload = { action, ...(reason ? { reason } : {}) };
    const result = await updateAdminTopupRequestStatus(request.id, payload, activeFilters);
    if (!result.success) {
      setInfoMessage(result.error || 'Failed to update top-up request');
    } else {
      setInfoMessage(action === 'approve' ? 'Top-up approved & wallet credited.' : 'Top-up rejected.');
    }
    setProcessingId('');
  };

  const stats = topupRequestStats || {};

  return (
    <motion.div
      className="glass-panel rounded-[18px] p-3 space-y-3"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      data-testid="admin-topup-requests-section"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-bold text-[#000000] font-['Outfit']">Top-Up Requests</h2>
        <button
          onClick={() => fetchRequests(activeFilters)}
          className="text-[11px] rounded-full border border-[#9333EA]/30 px-2.5 py-1 text-[#9333EA] font-semibold"
          data-testid="admin-topup-refresh-btn"
        >
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
        <div className="rounded-[10px] bg-white/70 border border-[#ececec] px-2 py-1.5">
          <p className="text-[10px] text-[#666666] font-['DM_Sans'] leading-tight">Total requests</p>
          <p className="text-sm font-bold text-[#111111] font-['Outfit']">{stats.totalRequests || 0}</p>
        </div>
        <div className="rounded-[10px] bg-white/70 border border-[#ececec] px-2 py-1.5">
          <p className="text-[10px] text-[#666666] font-['DM_Sans'] leading-tight">Pending</p>
          <p className="text-sm font-bold text-orange-600 font-['Outfit']">
            {stats.totalPending || 0} · ₹{formatAmount(stats.totalPendingAmount)}
          </p>
        </div>
        <div className="rounded-[10px] bg-white/70 border border-[#ececec] px-2 py-1.5">
          <p className="text-[10px] text-[#666666] font-['DM_Sans'] leading-tight">Approved (today)</p>
          <p className="text-sm font-bold text-green-600 font-['Outfit']">
            ₹{formatAmount(stats.totalApprovedAmountToday)}
          </p>
        </div>
        <div className="rounded-[10px] bg-white/70 border border-[#ececec] px-2 py-1.5">
          <p className="text-[10px] text-[#666666] font-['DM_Sans'] leading-tight">Approved (all)</p>
          <p className="text-sm font-bold text-green-600 font-['Outfit']">
            {stats.totalApproved || 0} · ₹{formatAmount(stats.totalApprovedAmount)}
          </p>
        </div>
        <div className="rounded-[10px] bg-white/70 border border-[#ececec] px-2 py-1.5">
          <p className="text-[10px] text-[#666666] font-['DM_Sans'] leading-tight">Rejected</p>
          <p className="text-sm font-bold text-[#111111] font-['Outfit']">{stats.totalRejected || 0}</p>
        </div>
      </div>

      <div className="rounded-[10px] bg-white/70 border border-[#ececec] p-2 space-y-2">
        <div className="flex items-center gap-1 text-[11px] text-[#666666] font-['DM_Sans']">
          <Filter className="w-3 h-3" /> Filters
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="rounded-[8px] border border-[#ececec] px-2 py-1.5 text-[11px]"
          />
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="rounded-[8px] border border-[#ececec] px-2 py-1.5 text-[11px]"
          />
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          <button
            onClick={() => fetchRequests(activeFilters)}
            className="rounded-full bg-[#9333EA] text-white text-[11px] font-semibold py-1.5"
          >
            Apply
          </button>
          <button
            onClick={resetFilters}
            className="rounded-full border border-[#9333EA]/30 text-[#9333EA] text-[11px] font-semibold py-1.5"
          >
            Reset
          </button>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          <button
            onClick={() => setPresetRange(1)}
            className="rounded-full border border-[#ececec] text-[#333333] text-[11px] font-semibold py-1.5"
          >
            Today
          </button>
          <button
            onClick={() => setPresetRange(7)}
            className="rounded-full border border-[#ececec] text-[#333333] text-[11px] font-semibold py-1.5"
          >
            Last 7 days
          </button>
        </div>
      </div>

      <div className="rounded-[10px] bg-white/70 border border-[#ececec] p-1">
        <div className="grid grid-cols-2 gap-1">
          <button
            onClick={() => setRequestTab('pending')}
            className={`py-1.5 rounded-[8px] text-xs font-semibold transition-colors ${
              requestTab === 'pending'
                ? 'bg-gradient-to-r from-[#FACC15] to-[#EAB308] text-[#3F2A00] shadow-sm'
                : 'text-[#666666] bg-white border border-[#FDE68A]'
            }`}
          >
            Pending / Fresh
          </button>
          <button
            onClick={() => setRequestTab('processed')}
            className={`py-1.5 rounded-[8px] text-xs font-semibold transition-colors ${
              requestTab === 'processed'
                ? 'bg-gradient-to-r from-[#FACC15] to-[#EAB308] text-[#3F2A00] shadow-sm'
                : 'text-[#666666] bg-white border border-[#FDE68A]'
            }`}
          >
            Processed
          </button>
        </div>
      </div>

      <div
        className="rounded-[12px] flex items-center gap-2 px-3 py-2 shadow-sm"
        style={{ backgroundColor: '#FFBF00' }}
        data-testid="admin-topup-search-wrap"
      >
        <Search className="w-4 h-4 text-[#3F2A00] flex-shrink-0" />
        <input
          type="text"
          value={noteSearch}
          onChange={(e) => setNoteSearch(e.target.value)}
          spellCheck={false}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          placeholder="Search by payer UPI ID"
          aria-label="Search top-up requests by payer UPI ID"
          className="flex-1 bg-transparent text-[13px] font-semibold text-[#3F2A00] placeholder-[#3F2A00]/60 focus:outline-none"
          data-testid="admin-topup-search-input"
        />
        {noteSearch && (
          <button
            type="button"
            onClick={() => setNoteSearch('')}
            className="text-[#3F2A00]/70 text-[11px] font-bold px-1"
            data-testid="admin-topup-search-clear"
            aria-label="Clear search"
          >
            ✕
          </button>
        )}
      </div>

      {infoMessage && (
        <p className="rounded-[10px] bg-white/80 px-2 py-1.5 text-[11px] text-[#333333] font-['DM_Sans']">
          {infoMessage}
        </p>
      )}

      <div className="space-y-2">
        {loading ? (
          <div className="rounded-[10px] bg-white/70 border border-[#ececec] p-2 text-[11px] text-[#666666] font-['DM_Sans']">
            Loading top-up requests...
          </div>
        ) : displayed.length === 0 ? (
          <div className="rounded-[10px] bg-white/70 border border-[#ececec] p-2 text-[11px] text-[#666666] font-['DM_Sans']">
            {requestTab === 'pending' ? 'No pending top-up requests.' : 'No processed top-up requests.'}
          </div>
        ) : (
          displayed.map((request) => {
            const status = String(request.status || 'pending').toLowerCase();
            const isPending = status === 'pending';
            const statusClass =
              status === 'approved'
                ? 'bg-green-100 text-green-700'
                : status === 'rejected'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-yellow-100 text-yellow-700';

            return (
              <div
                key={request.id}
                className="rounded-[12px] border border-[#ececec] bg-white/80 p-2 space-y-1.5"
                data-testid={`admin-topup-row-${request.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-[#111111] font-['Outfit'] truncate">
                      {request.userName || 'Unknown user'}
                    </p>
                    <p className="text-[11px] text-[#666666] font-['DM_Sans'] truncate">
                      {request.username || request.userId}
                    </p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${statusClass}`}>
                    {status}
                  </span>
                </div>

                <div className="rounded-[8px] bg-[#f7f7f7] p-1.5 text-[11px] font-['DM_Sans']">
                  <p className="text-[#777777]">Amount</p>
                  <p className="font-semibold text-[#111111]">₹{formatAmount(request.amount)}</p>
                </div>

                <p className="text-[11px] text-[#666666] font-['DM_Sans']">
                  {formatTimestamp(request.createdAt)}
                </p>

                <div className="rounded-[8px] bg-[#f7f7f7] p-1.5 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Smartphone className="w-3 h-3 text-[#666666]" />
                    <p className="text-[11px] text-[#111111] truncate">
                      <span className="text-[#666666]">To:</span> {request.upiId || 'NA'}
                    </p>
                  </div>
                  <button
                    onClick={() => handleCopy(`${request.id}-upi`, request.upiId)}
                    className="text-[#9333EA]"
                    data-testid={`admin-topup-copy-upi-${request.id}`}
                  >
                    {copiedKey === `${request.id}-upi` ? (
                      <Check className="w-3.5 h-3.5" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>

                <div className="rounded-[8px] bg-[#FFF7E0] p-1.5 flex items-center justify-between gap-2 border border-[#FFBF00]/40">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Smartphone className="w-3 h-3 text-[#9333EA]" />
                    <p className="text-[11px] text-[#111111] truncate">
                      <span className="text-[#666666]">From:</span> {request.payerUpiId || 'NA'}
                    </p>
                  </div>
                  <button
                    onClick={() => handleCopy(`${request.id}-payer`, request.payerUpiId)}
                    className="text-[#9333EA]"
                    data-testid={`admin-topup-copy-payer-${request.id}`}
                    aria-label="Copy payer UPI ID"
                  >
                    {copiedKey === `${request.id}-payer` ? (
                      <Check className="w-3.5 h-3.5" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>

                <div className="rounded-[8px] bg-[#f7f7f7] p-1.5 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Hash className="w-3 h-3 text-[#9333EA]" />
                    <p className="text-[11px] text-[#111111] truncate">
                      <span className="text-[#666666]">Note:</span> {request.orderNote || 'NA'}
                    </p>
                  </div>
                  <button
                    onClick={() => handleCopy(`${request.id}-note`, request.orderNote)}
                    className="text-[#9333EA]"
                    data-testid={`admin-topup-copy-note-${request.id}`}
                  >
                    {copiedKey === `${request.id}-note` ? (
                      <Check className="w-3.5 h-3.5" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>

                {request.reason && (
                  <p className="text-[11px] text-red-600 font-['DM_Sans']">
                    Reason: {request.reason}
                  </p>
                )}

                {isPending && (
                  <div className="flex items-center gap-2">
                    <label className="text-[11px] text-[#666666] font-['DM_Sans']">Action</label>
                    <select
                      defaultValue=""
                      onChange={(e) => {
                        const action = e.target.value;
                        if (!action) return;
                        handleAction(request, action);
                        e.target.value = '';
                      }}
                      disabled={processingId === request.id}
                      className="flex-1 rounded-full border border-[#9333EA]/30 bg-white px-3 py-1.5 text-[11px] font-semibold text-[#9333EA] disabled:opacity-50"
                      data-testid={`admin-topup-action-select-${request.id}`}
                    >
                      <option value="">Process request</option>
                      <option value="approve">Approve (credit wallet)</option>
                      <option value="reject">Reject</option>
                    </select>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </motion.div>
  );
};

export default AdminTopupRequestsSection;
